import React from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import type { Team, User } from '@kangaroo/shared';
import { AdminAccessGate } from '../pages/admin/AdminAccessGate';
import { BillingPage } from '../pages/billing/BillingPage';
import { legacyBillingDestination } from '../lib/billingNavigation';
import { HELP_PAGES_ENABLED, LIVE_BETA_ENABLED } from '../lib/featureFlags';
import { RouteContent } from './RouteContent';
import { DriverDetail } from '../pages/drivers/DriverDetail';
import { TeamManagementPage } from '../pages/team-management/TeamManagementPage';
import { BillingRecoveryPage } from '../pages/billing/BillingRecoveryPage';
import { MaintenanceQueuePage } from '../pages/maintenance/MaintenanceQueuePage';
import { TrackEnrichmentPage } from '../pages/dev/TrackEnrichmentPage';
import { GoProFootageDevPage } from '../pages/dev/GoProFootageDevPage';
import { PerformanceIndexDevPage } from '../pages/dev/PerformanceIndexDevPage';
import { DevtoolsPage } from '../pages/dev/DevtoolsPage';
import { CircuitsPage } from '../pages/circuits/CircuitsPage';
import { UploadsPage } from '../pages/data/UploadsPage';
import { KnowledgeBasePage } from '../pages/help/KnowledgeBasePage';
import { TeamOnboardingPage } from '../pages/onboarding/TeamOnboardingPage';
import { CoachingPage } from '../pages/coaching/CoachingPage';
import { LeaderboardPage } from '../pages/leaderboard/LeaderboardPage';
import { CircuitDetailPage } from '../pages/circuits/CircuitDetailPage';
const KartsPage = React.lazy(() =>
  import('../pages/karts/KartsPage').then((module) => ({ default: module.KartsPage })),
);
const SessionsPage = React.lazy(() =>
  import('../pages/sessions/SessionsPage').then((module) => ({ default: module.SessionsPage })),
);
const AdminPage = React.lazy(() =>
  import('../pages/admin/AdminPage').then((module) => ({ default: module.AdminPage })),
);
const TeamBillingPage = React.lazy(() =>
  import('../pages/admin/TeamBillingPage').then((module) => ({ default: module.TeamBillingPage })),
);
const CircuitMakerPage = React.lazy(() =>
  import('../pages/admin/CircuitMakerPage').then((module) => ({
    default: module.CircuitMakerPage,
  })),
);
const TrackCatalogPage = React.lazy(() =>
  import('../pages/admin/TrackCatalogPage').then((module) => ({
    default: module.TrackCatalogPage,
  })),
);
const LiveBetaPage = LIVE_BETA_ENABLED
  ? React.lazy(async () => {
      const module = await import('../pages/dev/LiveBetaPage');
      return { default: module.LiveBetaPage };
    })
  : null;

function helpRoutes(fallbackPath: string) {
  if (HELP_PAGES_ENABLED) {
    return (
      <>
        <Route path="/help" element={<KnowledgeBasePage />} />
        <Route path="/help/:slug" element={<KnowledgeBasePage />} />
      </>
    );
  }

  return (
    <>
      <Route path="/help" element={<Navigate to={fallbackPath} replace />} />
      <Route path="/help/:slug" element={<Navigate to={fallbackPath} replace />} />
    </>
  );
}

function PlansRedirect() {
  const location = useLocation();
  return <Navigate to={legacyBillingDestination('/plans', location.search)!} replace />;
}

function RedirectToSessions() {
  const location = useLocation();
  return <Navigate to={`/sessions${location.search}`} replace />;
}

export function WorkspaceRoutes({
  mode,
  user,
  currentTeam,
  historicalSessionsOnly = false,
}: {
  mode: 'setup' | 'billing-recovery' | 'ready';
  user: User;
  currentTeam?: Team;
  historicalSessionsOnly?: boolean;
}) {
  const fallbackPath = mode === 'setup' ? '/setup-team' : '/drivers';
  return (
    <RouteContent fallbackPath={fallbackPath} scope={`${user.id}:${user.teamId ?? ''}:${mode}`}>
      {mode === 'setup' ? (
        <Routes>
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/settings" element={null} />
          {helpRoutes('/setup-team')}
          <Route
            path="/setup-team"
            element={<TeamOnboardingPage user={user!} provisionalTeam={currentTeam ?? null} />}
          />
          <Route path="*" element={<Navigate to="/setup-team" replace />} />
        </Routes>
      ) : mode === 'billing-recovery' ? (
        <Routes>
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/drivers" element={<BillingRecoveryPage />} />
          <Route path="/plans" element={<PlansRedirect />} />
          <Route path="/settings" element={null} />
          {helpRoutes('/drivers')}
          <Route path="*" element={<Navigate to="/drivers?billing=1" replace />} />
        </Routes>
      ) : (
        <Routes>
          <Route path="/billing" element={<BillingPage />} />
          <Route path="/" element={<Navigate to="/drivers" replace />} />
          <Route path="/dashboard" element={<Navigate to="/drivers" replace />} />
          <Route path="/home" element={<Navigate to="/drivers" replace />} />
          <Route path="/drivers" element={<TeamManagementPage />} />
          <Route path="/drivers/:id" element={<DriverDetail />} />
          <Route path="/karts" element={<KartsPage />} />
          <Route path="/data" element={<Navigate to="/sessions" replace />} />
          <Route path="/circuits" element={<CircuitsPage />} />
          <Route path="/circuits/:circuitId" element={<CircuitDetailPage />} />
          <Route path="/telemetry" element={<RedirectToSessions />} />
          <Route path="/coaching" element={<CoachingPage />} />
          <Route path="/team-management" element={<TeamManagementPage />} />
          <Route path="/plans" element={<PlansRedirect />} />
          <Route path="/sessions" element={<SessionsPage readOnly={historicalSessionsOnly} />} />
          {LiveBetaPage ? <Route path="/live" element={<LiveBetaPage />} /> : null}
          {import.meta.env.DEV ? <Route path="/devtools" element={<DevtoolsPage />} /> : null}
          <Route
            path="/uploads"
            element={import.meta.env.DEV ? <UploadsPage /> : <Navigate to="/sessions" replace />}
          />
          <Route path="/leaderboard" element={<LeaderboardPage />} />
          <Route path="/compare" element={<Navigate to="/sessions" replace />} />
          <Route path="/reports" element={<Navigate to="/sessions" replace />} />
          <Route path="/events" element={<Navigate to="/sessions" replace />} />
          <Route path="/tasks" element={<Navigate to="/sessions" replace />} />
          <Route path="/messages" element={<Navigate to="/sessions" replace />} />
          <Route path="/inventory" element={<Navigate to="/sessions" replace />} />
          <Route path="/maintenance" element={<MaintenanceQueuePage />} />
          {helpRoutes('/drivers')}
          <Route
            path="/admin"
            element={
              <AdminAccessGate user={user}>
                <AdminPage />
              </AdminAccessGate>
            }
          />
          <Route
            path="/admin/:section"
            element={
              <AdminAccessGate user={user}>
                <AdminPage />
              </AdminAccessGate>
            }
          />
          <Route
            path="/admin/teams/:teamId/billing"
            element={
              <AdminAccessGate user={user}>
                <TeamBillingPage />
              </AdminAccessGate>
            }
          />
          <Route
            path="/admin/circuit-maker"
            element={
              <AdminAccessGate user={user}>
                <CircuitMakerPage />
              </AdminAccessGate>
            }
          />
          <Route
            path="/admin/track-catalog"
            element={
              <AdminAccessGate user={user}>
                <TrackCatalogPage />
              </AdminAccessGate>
            }
          />
          {import.meta.env.DEV ? (
            <>
              <Route path="/dev/track-enrichment" element={<TrackEnrichmentPage />} />
              <Route path="/dev/gopro-footage" element={<GoProFootageDevPage />} />
              <Route path="/dev/performance-index" element={<PerformanceIndexDevPage />} />
            </>
          ) : null}
          <Route path="/settings" element={null} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      )}
    </RouteContent>
  );
}
